import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getStudentUsers,
  updateStudentUserStatus,
  getUserActivity,
  getStudentProfile,
  getStudentRides,
  setAcademicVerification,
  removeAcademicVerification,
} from '../services/userService';
import ActionsMenu from '../components/ActionsMenu';
import ConfirmDialog from '../components/ConfirmDialog';
import { useCampuses } from '../components/statistics/PeriodBar';
import { formatDateTime, formatDecimal } from '../utils/period';
import './Users.css';
import '../pages/Invitations.css';

interface Member {
  id: number;
  fullName: string;
  email: string;
  status: string;
  campus: string | null;
  academicEmail: string | null;
  academicEmailVerified: boolean;
  /** When academic verification first happened. Kept after the academic email is
   *  changed or removed; NULL for students verified before it was recorded. */
  academicVerifiedAt: string | null;
  /** Verified by staff without an academic email (from the staff audit log). */
  verifiedWithoutEmail: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  lastSeenAt: string | null;
  ridesOffered: number;
  ridesTaken: number;
  ratingsGiven: number;
  ratingsReceived: number;
  averageRating: number | null;
  cancellations: number;
}

/**
 * The "Academic email verified at" cell. The date outlives the verification (it
 * stays when the academic email is changed or removed), and students verified
 * before it was recorded have none — so the flag decides which wording applies.
 */
function academicVerifiedText(member: Member): string {
  const at = member.academicVerifiedAt;
  if (member.academicEmailVerified) {
    const when = at ? formatDateTime(at) : 'Yes, date unknown';
    return member.verifiedWithoutEmail ? `${when} (by staff, no email)` : when;
  }
  return at ? `No (was ${formatDateTime(at)})` : 'Never';
}

interface AuditEntry {
  id: number;
  action: string;
  details: { from?: string | null; to?: string | null; method?: string; reason?: string } | null;
  createdAt: string;
  actor: { id: number; fullName: string } | null;
}

interface ActivityDialogState {
  isOpen: boolean;
  user: Member | null;
  activity: any;
  loading: boolean;
}

interface ConfirmState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: (() => void) | null;
}

const CLOSED_CONFIRM: ConfirmState = { isOpen: false, title: '', message: '', onConfirm: null };

interface VerifyDialogState {
  user: Member;
  email: string;
  /** Required when verifying without an academic email. */
  reason: string;
  saving: boolean;
  error: string | null;
}

interface ProfileDialogState {
  user: Member;
  profile: any;
  loading: boolean;
}

/** 'bookings' = the Taken list with "Show all bookings" on (any status). */
type RideKind = 'offered' | 'taken' | 'bookings';

interface RidesDialogState {
  user: Member;
  kind: RideKind;
  rides: any[];
  truncated: boolean;
  loading: boolean;
}

/** "2026-10-08" + "07:30:00" → "8 Oct 2026, 07:30" — the ride's own wall time, no zone shift. */
function departureText(ride: any): string {
  if (!ride.departureDate) return '—';
  const [y, m, d] = String(ride.departureDate).split('-').map(Number);
  const day = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return ride.departureTime ? `${day}, ${String(ride.departureTime).slice(0, 5)}` : day;
}

/** Day-resolution timestamps (last seen) as a date on Israel's calendar: "8 Oct 2026". */
const formatIsraelDay = (value: string) =>
  new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Jerusalem' });

const priceText = (price: number | null) => (price === null ? '—' : price === 0 ? 'Free' : `₪${formatDecimal(price)}`);

/** How a staff change reads in the Activity dialog's audit list. */
function auditText(entry: AuditEntry): string {
  const d = entry.details;
  switch (entry.action) {
    case 'status_change':
      return `Status ${d?.from ?? '?'} → ${d?.to ?? '?'}`;
    case 'academic_verify':
      if (d?.method === 'no_email') return `Verified without an academic email: ${d.reason ?? ''}`;
      return (
        (d?.from && d.from !== d.to
          ? `Academic email set to ${d?.to} (was ${d.from}) and verified`
          : `Academic email ${d?.to ?? ''} verified`) + (d?.reason ? `: ${d.reason}` : '')
      );
    case 'academic_unverify':
      return `Academic verification removed${d?.from ? ` (${d.from})` : ''}`;
    default:
      return entry.action;
  }
}

const yesNo = (value: unknown) => (value ? 'Yes' : 'No');

/**
 * Phones are stored in E.164 (+972501234567); staff read them in Israeli local
 * form, 050-123-4567. Anything else is shown as stored.
 */
const formatPhoneLocal = (value: string | null | undefined): string => {
  const match = /^\+972(5\d)(\d{3})(\d{4})$/.exec(value || '');
  return match ? `0${match[1]}-${match[2]}-${match[3]}` : value || '';
};

/**
 * A sortable column header: click to sort by it, click again to flip. The active
 * column shows ▲ (ascending) or ▼ (descending); the others a faint ↕.
 */
function SortHeader({
  field,
  label,
  title,
  sort,
  onSort,
}: {
  field: string;
  label: string;
  title?: string;
  sort: { by: string; order: string };
  onSort: (field: string) => void;
}) {
  const active = sort.by === field;
  const ascending = sort.order === 'ASC';
  return (
    <th title={title} aria-sort={active ? (ascending ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={`sort-header${active ? ' active' : ''}`} onClick={() => onSort(field)}>
        {label}
        <span className="sort-chevron" aria-hidden="true">
          {active ? (ascending ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

const errorText = (err: any, fallback: string) => err?.response?.data?.message || fallback;

const statusBadgeClass = (status: string) =>
  status === 'pending' ? 'badge-pending' : status === 'active' ? 'badge-accepted' : status === 'inactive' ? 'badge-expired' : '';

/**
 * People (PRD §5.6): every student, with campus, verification, last seen and
 * what they have done on the platform. "#123" in the search finds member 123,
 * which is how the statistics screens link here.
 */
const Users = () => {
  const [searchParams] = useSearchParams();
  const campuses = useCampuses();
  const campusName = (id: string | null) => (id ? campuses.find((c) => c.id === id)?.name || id : '—');

  const [users, setUsers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const limit = 20;
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [campusFilter, setCampusFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState('');
  const [joinedBefore, setJoinedBefore] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  const [activityDialog, setActivityDialog] = useState<ActivityDialogState>({
    isOpen: false,
    user: null,
    activity: null,
    loading: false,
  });
  const [confirmDialog, setConfirmDialog] = useState<ConfirmState>(CLOSED_CONFIRM);
  const [verifyDialog, setVerifyDialog] = useState<VerifyDialogState | null>(null);
  const [profileDialog, setProfileDialog] = useState<ProfileDialogState | null>(null);
  const [ridesDialog, setRidesDialog] = useState<RidesDialogState | null>(null);

  // A statistics screen linking here with ?search=#id while People is already open.
  const linkedSearch = searchParams.get('search');
  useEffect(() => {
    if (linkedSearch !== null) setSearchInput(linkedSearch);
  }, [linkedSearch]);

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, searchQuery, campusFilter, verifiedFilter, activityFilter, joinedBefore, sortBy, sortOrder]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string | number> = { page, limit, sortBy, sortOrder };
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      if (campusFilter) params.campus = campusFilter;
      if (verifiedFilter) params.verified = verifiedFilter;
      if (activityFilter) params.activity = activityFilter;
      if (joinedBefore) params.joinedBefore = joinedBefore;

      const response = await getStudentUsers(params);
      setUsers(response.data.users || []);
      setPagination(response.data.pagination || { total: 0, pages: 1 });
    } catch (err) {
      setError(errorText(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  const sort = { by: sortBy, order: sortOrder };

  /** Header click: the same column flips direction; a new one starts at its natural order. */
  const handleSort = (field: string) => {
    if (field === sortBy) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder(field === 'fullName' ? 'ASC' : 'DESC');
    }
    setPage(1);
  };

  const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  const handleToggleUserStatus = (user: Member) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activate' : 'deactivate';

    setConfirmDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      message: `Are you sure you want to ${action} ${user.fullName || 'this user'}? ${
        newStatus === 'inactive' ? 'They will no longer be able to access the system.' : 'They will regain access to the system.'
      }`,
      onConfirm: async () => {
        setConfirmDialog(CLOSED_CONFIRM);
        try {
          await updateStudentUserStatus(user.id, newStatus);
          setSuccess(`User ${action}d successfully`);
          setTimeout(() => setSuccess(null), 5000);
          loadUsers();
        } catch (err) {
          setError(errorText(err, `Failed to ${action} user`));
        }
      },
    });
  };

  const handleShowActivity = async (user: Member) => {
    setActivityDialog({ isOpen: true, user, activity: null, loading: true });
    try {
      const response = await getUserActivity(user.id);
      setActivityDialog((prev) => ({ ...prev, activity: response.data, loading: false }));
    } catch (err) {
      setActivityDialog((prev) => ({ ...prev, loading: false }));
      setError(errorText(err, 'Failed to load user activity'));
    }
  };

  const flashSuccess = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleShowRides = async (user: Member, kind: RideKind) => {
    setRidesDialog({ user, kind, rides: [], truncated: false, loading: true });
    try {
      const response = await getStudentRides(user.id, kind);
      setRidesDialog({ user, kind, rides: response.data.rides || [], truncated: !!response.data.truncated, loading: false });
    } catch (err) {
      setRidesDialog(null);
      setError(errorText(err, 'Failed to load rides'));
    }
  };

  const handleViewProfile = async (user: Member) => {
    setProfileDialog({ user, profile: null, loading: true });
    try {
      const response = await getStudentProfile(user.id);
      setProfileDialog({ user, profile: response.data.user, loading: false });
    } catch (err) {
      setProfileDialog(null);
      setError(errorText(err, 'Failed to load profile'));
    }
  };

  const submitVerify = async () => {
    if (!verifyDialog) return;
    const { user, email, reason } = verifyDialog;
    setVerifyDialog({ ...verifyDialog, saving: true, error: null });
    try {
      await setAcademicVerification(user.id, email.trim(), reason.trim());
      setVerifyDialog(null);
      flashSuccess(`${user.fullName || 'Member'} is now academically verified`);
      loadUsers();
    } catch (err) {
      setVerifyDialog((prev) => prev && { ...prev, saving: false, error: errorText(err, 'Failed to verify') });
    }
  };

  const handleUnverify = (user: Member) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove academic verification',
      message: `${user.fullName || 'This member'} will no longer be able to offer or book rides until they verify ${
        user.academicEmail || 'an academic email'
      } again from the app. The address stays on their account.`,
      onConfirm: async () => {
        setConfirmDialog(CLOSED_CONFIRM);
        try {
          await removeAcademicVerification(user.id);
          flashSuccess('Academic verification removed');
          loadUsers();
        } catch (err) {
          setError(errorText(err, 'Failed to remove verification'));
        }
      },
    });
  };

  const closeActivityDialog = () => setActivityDialog({ isOpen: false, user: null, activity: null, loading: false });

  const auditLog: AuditEntry[] = activityDialog.activity?.auditLog || [];

  return (
    <div className="invitations-page">
      <div className="invitations-header">
        <div>
          <h1>People</h1>
          <p className="subtitle">Every student, what they have done on the platform, and when they were last seen</p>
        </div>
      </div>

      {success && (
        <div style={{ marginBottom: '20px' }}>
          <div className="alert alert-success">{success}</div>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: '20px' }}>
          <div className="alert alert-error">{error}</div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="filters">
            <input
              type="text"
              placeholder="Search by name or email, or #id"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="search-input"
            />

            <select value={campusFilter} onChange={(e) => resetPage(setCampusFilter)(e.target.value)} className="filter-select">
              <option value="">All campuses</option>
              {campuses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <select value={verifiedFilter} onChange={(e) => resetPage(setVerifiedFilter)(e.target.value)} className="filter-select">
              <option value="">Verified or not</option>
              <option value="yes">Academic email verified</option>
              <option value="no">Not verified</option>
            </select>

            <select value={activityFilter} onChange={(e) => resetPage(setActivityFilter)(e.target.value)} className="filter-select">
              <option value="">Any activity</option>
              <option value="never">Never offered or took a ride</option>
              <option value="rider">Rider only</option>
              <option value="driver">Driver only</option>
              <option value="both">Rider and driver</option>
            </select>

            <label className="filter-date">
              <span>Joined before</span>
              <input
                type="date"
                value={joinedBefore}
                onChange={(e) => resetPage(setJoinedBefore)(e.target.value)}
                className="filter-select"
              />
            </label>

            <select value={statusFilter} onChange={(e) => resetPage(setStatusFilter)(e.target.value)} className="filter-select">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
        </div>

        {loading && <div className="loading">Loading users...</div>}

        {!loading && !error && users.length === 0 && (
          <div className="empty-state">
            <p>No users found.</p>
          </div>
        )}

        {!loading && !error && users.length > 0 && (
          <>
            <div className="table-container">
              <table className="invitations-table">
                <thead>
                  <tr>
                    <SortHeader field="fullName" label="Name" sort={sort} onSort={handleSort} />
                    <th>Campus</th>
                    <th>Academic email verified at</th>
                    <th>Status</th>
                    <SortHeader field="createdAt" label="Joined" sort={sort} onSort={handleSort} />
                    <SortHeader
                      field="lastSeen"
                      label="Last seen"
                      title="Last day the member used the app (Israel time)"
                      sort={sort}
                      onSort={handleSort}
                    />
                    <SortHeader field="ridesOffered" label="Offered Rides" sort={sort} onSort={handleSort} />
                    <SortHeader field="ridesTaken" label="Taken Rides" sort={sort} onSort={handleSort} />
                    <th>Ratings (given / received)</th>
                    <th>Cancellations</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div>{user.fullName || '—'}</div>
                        <div className="member-email">{user.email}</div>
                      </td>
                      <td>{campusName(user.campus)}</td>
                      <td>{academicVerifiedText(user)}</td>
                      <td>
                        <span className={`badge ${statusBadgeClass(user.status)}`}>{user.status}</span>
                      </td>
                      <td>{formatDateTime(user.createdAt)}</td>
                      <td>{user.lastSeenAt ? formatIsraelDay(user.lastSeenAt) : 'Never'}</td>
                      <td className="numeric">
                        {user.ridesOffered > 0 ? (
                          <button type="button" className="count-link" onClick={() => handleShowRides(user, 'offered')}>
                            {user.ridesOffered}
                          </button>
                        ) : (
                          0
                        )}
                      </td>
                      <td className="numeric">
                        {user.ridesTaken > 0 ? (
                          <button type="button" className="count-link" onClick={() => handleShowRides(user, 'taken')}>
                            {user.ridesTaken}
                          </button>
                        ) : (
                          0
                        )}
                      </td>
                      <td className="numeric">
                        {user.ratingsGiven} / {user.ratingsReceived}
                        {user.averageRating !== null && ` (avg ${formatDecimal(user.averageRating)})`}
                      </td>
                      <td className="numeric">{user.cancellations}</td>
                      <td>
                        <ActionsMenu
                          items={[
                            { label: 'View profile', onSelect: () => handleViewProfile(user) },
                            { label: 'Staff changes', onSelect: () => handleShowActivity(user) },
                            {
                              label: user.academicEmailVerified
                                ? 'Change verified academic email…'
                                : 'Set as academically verified…',
                              onSelect: () =>
                                setVerifyDialog({ user, email: user.academicEmail || '', reason: '', saving: false, error: null }),
                              hidden: user.status === 'deleted',
                            },
                            {
                              label: 'Remove academic verification',
                              onSelect: () => handleUnverify(user),
                              danger: true,
                              hidden: !user.academicEmailVerified || user.status === 'deleted',
                            },
                            {
                              label: user.status === 'active' ? 'Deactivate' : 'Activate',
                              onSelect: () => handleToggleUserStatus(user),
                              danger: user.status === 'active',
                              disabled: user.status === 'pending',
                              hidden: user.status === 'deleted',
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button onClick={() => setPage(page - 1)} disabled={page === 1} className="btn btn-secondary btn-sm">
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {Math.max(1, pagination.pages)} ({pagination.total} total users)
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {activityDialog.isOpen && (
        <div className="modal-overlay" onClick={closeActivityDialog}>
          <div className="modal-content activity-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Staff changes: {activityDialog.user?.fullName || activityDialog.user?.email}</h2>
              <button className="modal-close" onClick={closeActivityDialog}>
                &times;
              </button>
            </div>

            {activityDialog.loading && <div className="loading">Loading changes...</div>}

            {!activityDialog.loading && activityDialog.activity && (
              <div className="activity-content">
                <div className="activity-section">
                  <h3>Changes to this account by staff ({auditLog.length})</h3>
                  {auditLog.length > 0 ? (
                    <ul className="audit-list">
                      {auditLog.map((entry) => (
                        <li key={entry.id}>
                          <strong>{auditText(entry)}</strong>{' '}
                          by {entry.actor?.fullName || 'a former staff member'}, {formatDateTime(entry.createdAt)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-activity">No changes recorded</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {ridesDialog && (
        <div className="modal-overlay" onClick={() => setRidesDialog(null)}>
          <div className="modal-content rides-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {ridesDialog.kind === 'offered' ? 'Offered rides' : ridesDialog.kind === 'taken' ? 'Taken rides' : 'All bookings'}:{' '}
                {ridesDialog.user.fullName || ridesDialog.user.email}
              </h2>
              <button className="modal-close" onClick={() => setRidesDialog(null)}>
                &times;
              </button>
            </div>
            <div className="rides-content">
              {ridesDialog.kind !== 'offered' && (
                <label className="rides-toggle">
                  <input
                    type="checkbox"
                    checked={ridesDialog.kind === 'bookings'}
                    disabled={ridesDialog.loading}
                    onChange={(e) => handleShowRides(ridesDialog.user, e.target.checked ? 'bookings' : 'taken')}
                  />
                  Show all bookings (also pending, rejected, cancelled, expired)
                </label>
              )}
              {ridesDialog.loading && <div className="loading">Loading rides...</div>}
              {!ridesDialog.loading && ridesDialog.rides.length === 0 && <p className="no-activity">No rides</p>}
              {!ridesDialog.loading && ridesDialog.rides.length > 0 && (
                <>
                  <p className="rides-note">
                    {ridesDialog.kind === 'offered'
                      ? 'Every ride they drove, in any status. Newest departure first.'
                      : ridesDialog.kind === 'taken'
                        ? 'Bookings that were confirmed or completed — the rides counted in Taken Rides. Newest departure first.'
                        : 'Every booking they made, in any status. Newest departure first.'}
                    {ridesDialog.truncated && ` Showing the latest ${ridesDialog.rides.length}.`}
                  </p>
                  <table className="rides-table">
                    <thead>
                      <tr>
                        <th>Ride</th>
                        <th>Departure</th>
                        <th>From</th>
                        <th>To</th>
                        {ridesDialog.kind === 'offered' ? <th>Seats filled</th> : <th>Driver</th>}
                        <th>Price</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ridesDialog.rides.map((ride) => (
                        <tr key={ridesDialog.kind === 'offered' ? ride.rideId : ride.bookingId}>
                          <td className="numeric">#{ride.rideId}</td>
                          <td>{departureText(ride)}</td>
                          <td>{ride.origin || '—'}</td>
                          <td>{ride.destination || '—'}</td>
                          {ridesDialog.kind === 'offered' ? (
                            <td className="numeric">
                              {ride.seatsFilled} / {ride.seatsOffered}
                            </td>
                          ) : (
                            <td>
                              {ride.driverName || '—'}
                              {ride.driverId !== null && <span className="member-email"> #{ride.driverId}</span>}
                            </td>
                          )}
                          <td className="numeric">{priceText(ride.price)}</td>
                          <td>
                            {ride.status}
                            {ridesDialog.kind !== 'offered' && ride.rideStatus && ride.rideStatus !== ride.status && (
                              <span className="member-email"> (ride {ride.rideStatus})</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {verifyDialog && (
        <div className="modal-overlay" onClick={() => !verifyDialog.saving && setVerifyDialog(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Set as academically verified</h2>
              <button className="modal-close" onClick={() => setVerifyDialog(null)} disabled={verifyDialog.saving}>
                &times;
              </button>
            </div>
            <form
              className="modal-form"
              onSubmit={(e) => {
                e.preventDefault();
                submitVerify();
              }}
            >
              <p className="verify-intro">
                {verifyDialog.user.fullName || 'This member'} ({verifyDialog.user.email}) will be marked as a verified
                student, without the email link. Enter their academic email, or clear it and give the reason you are
                verifying them without one. This is recorded under your name.
              </p>
              {verifyDialog.error && <div className="modal-alert alert-error">{verifyDialog.error}</div>}
              <div className="form-group">
                <label htmlFor="verify-academic-email">Academic email (optional)</label>
                <input
                  id="verify-academic-email"
                  type="email"
                  value={verifyDialog.email}
                  onChange={(e) => setVerifyDialog({ ...verifyDialog, email: e.target.value })}
                  placeholder="name@post.bgu.ac.il"
                  disabled={verifyDialog.saving}
                  autoFocus
                />
              </div>
              {/* Always shown — the email is pre-filled from the account, so hiding
                  this until the email is cleared made it look missing. */}
              <div className="form-group">
                <label htmlFor="verify-reason">
                  {verifyDialog.email.trim() ? 'Reason (optional)' : 'Reason (required without an academic email)'}
                </label>
                <textarea
                  id="verify-reason"
                  className="verify-reason"
                  value={verifyDialog.reason}
                  onChange={(e) => setVerifyDialog({ ...verifyDialog, reason: e.target.value })}
                  placeholder="e.g. Saw their student card in person"
                  maxLength={500}
                  rows={3}
                  disabled={verifyDialog.saving}
                  required={!verifyDialog.email.trim()}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setVerifyDialog(null)} disabled={verifyDialog.saving}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={verifyDialog.saving || (!verifyDialog.email.trim() && !verifyDialog.reason.trim())}
                >
                  {verifyDialog.saving ? 'Saving…' : 'Verify'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {profileDialog && (
        <div className="modal-overlay" onClick={() => setProfileDialog(null)}>
          <div className="modal-content profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Profile: {profileDialog.user.fullName || profileDialog.user.email}</h2>
              <button className="modal-close" onClick={() => setProfileDialog(null)}>
                &times;
              </button>
            </div>
            {profileDialog.loading && <div className="loading">Loading profile...</div>}
            {!profileDialog.loading && profileDialog.profile && (
              <div className="profile-content">
                {profileDialog.profile.photoUrl && (
                  <img className="profile-photo" src={profileDialog.profile.photoUrl} alt="" />
                )}
                <dl className="profile-fields">
                  {(
                    [
                      ['Member #', profileDialog.profile.id],
                      ['Full name', profileDialog.profile.fullName || '—'],
                      ['Email', profileDialog.profile.email],
                      ['Email verified', yesNo(profileDialog.profile.emailVerified)],
                      ['Phone', formatPhoneLocal(profileDialog.profile.phoneNumber) || '—'],
                      ['Gender', profileDialog.profile.gender || '—'],
                      ['Date of birth', profileDialog.profile.dateOfBirth || '—'],
                      ['Campus', campusName(profileDialog.profile.institution)],
                      ['Academic email', profileDialog.profile.academicEmail || '—'],
                      ['Academic email verified at', academicVerifiedText({ ...profileDialog.user, academicEmailVerified: !!profileDialog.profile.academicEmailVerified, academicVerifiedAt: profileDialog.profile.academicVerifiedAt })],
                      ['Status', profileDialog.profile.status],
                      ['Signed up with', profileDialog.profile.signupMethod || '—'],
                      ['Google linked', yesNo(profileDialog.profile.googleId)],
                      ['Apple linked', yesNo(profileDialog.profile.appleId)],
                      ['Joined', formatDateTime(profileDialog.profile.createdAt)],
                      ['Last login', profileDialog.profile.lastLoginAt ? formatDateTime(profileDialog.profile.lastLoginAt) : '—'],
                      ['Terms accepted', profileDialog.profile.termsAcceptedAt ? formatDateTime(profileDialog.profile.termsAcceptedAt) : '—'],
                      ['Profile complete', yesNo(profileDialog.profile.profileComplete)],
                    ] as [string, string | number][]
                  ).map(([label, value]) => (
                    <div key={label} className="profile-field">
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(CLOSED_CONFIRM)}
        variant="danger"
      />
    </div>
  );
};

export default Users;
